import React, {
  FunctionComponent,
  useContext,
  useState,
  useRef,
  useCallback,
  useEffect,
} from 'react';
import { navigate } from 'gatsby';
import { useOutageEvents } from './useOutageEvents';
import {
  AccountDetail,
  OutageEvent,
  OutageLookupResponse,
  OutageLookUpType,
  PremiseInfo,
  ReportOutageSource,
  ZipCodeInfo,
  Maybe,
  ReportOutageResponse,
} from '../../__generated__/pge-types';
import {
  Button,
  Card,
  createStyles,
  Grid,
  IconButton,
  makeStyles,
  Theme,
} from '@material-ui/core';
import clsx from 'clsx';
import AccountCircleOutlined from '@material-ui/icons/AccountCircleOutlined';
import useUtils from './outage-center/utils';
import OutageCenterMap from './outage-center-map/OutageCenterMap';
import { useTranslation } from '../../hooks/useTranslation';
import Backdrop from '../backdrop';
import OutageMapLegendIcon from '../iconsButtons/OutageMapLegendIcon';
import OutageMapLegendPopup from './outage-center/outage-map-legend-popup';
import OutageMapPanel from './outage-center/outage-map-panel/OutageMapPanel';
import OutageResultsPanel, {
  OutageResultPanelData,
} from './outage-center/outage-map-panel/OutageResultsPanel';
import OutageReportPopup from './outage-center/outage-report-popup';
import { OutageCheckBreakersPopup } from './outage-center/outage-report-popup/OutageCheckBreakerPopup/OutageCheckBreakersPopup';
import OutageMapHeader from './outage-center/header/header';
import colors from '../../themes/main-colors';
import { useIsMobile } from '../../util/style-utils';
import ChangeNotificationSettings from './outage-center/outage-notifications-popup/ChangeNotificationSettings';
import OutageListAndZipPanel from './outage-center/outage-accounts-and-zip-panel/OutageListAndZipPanel';
import MultiplePremise from './outage-center/multiple-premise-popup/multiplePremise';
import { useReportOutageApi } from './outage-center/outage-map-panel/useOutageReportApi';
import ReportOutageIcon from './outage-center/reportOutageIcon/ReportOutageIcon';
import OutageReportPopupMobApp from './outage-center/outage-report-popup/OutageReportPopup-Mob-App/OutageReportPopupMobApp';
import useWrapWithLoader from '../../hooks/useWrapWithLoading';
import useOutageMapNotificationVisiblity from '../../hooks/useOutagesMapNotificationVisiblity';
import { Model } from './report-outage/serviceLocation.rules';
import useAuth from '../../hooks/useAuth';
import ROUTES from '../../routes';
import OutageMapFaqModal from './outage-center/outage-map-FAQ-accordion/outageMapFaqModal';
import { featureFlagsContext } from '../../providers/FeatureFlagsProvider';
import OutageFindAccountPopup from './outage-center/outage-find-account-popup/OutageFindAccountPopup';
import { LanguageContext } from '../../providers/LanguageProvider';
import OutageCheckBreakersReportPopupMob from './outage-center/outage-report-popup/OutageCheckBreakerPopup/OutageCheckBreakersReportPopupMob';
import ResetBreakerConfirm from './outage-center/outage-report-popup/ResetBreakerConfirm/ResetBreakerConfirm';
import OutageType from './outage-center/outage-report-popup/OutageType/OutageType';
import DimOrFlickering from './outage-center/outage-report-popup/DimOrFlickering/DimOrFlickering';
import OutageTypeMob from './outage-center/outage-report-popup/OutageType/OutageTypeMob';
import DimOrFlickeringMob from './outage-center/outage-report-popup/DimOrFlickering/DimOrFlickeringMob';
import ResetBreakerConfirmMob from './outage-center/outage-report-popup/ResetBreakerConfirm/ResetBreakerConfirmMob';
import { useUpdateInfoApi } from '../update-info/useUpdateInfoApi';
import OutageReportedConfirmPopup from './outage-center/outage-report-popup/OutageReportedConfirm/OutageReportedConfirmPopup';
import { setOutageReportedStatusForMedallia } from '../../lib/gtmService';
import { formatEstimatedTimeOn } from './outageUtils';
import useContentMgmt from '../../hooks/useContentMgmt';
import microcopyGroupIds from '../pge-plus-common/microcopyGroupIds';
import SelectAccount from './outage-center/outage-report-popup/SelectAccount/SelectAccount';
import SelectAccountMob from './outage-center/outage-report-popup/SelectAccount/SelectAccountMob';
import ReconnectPopup from './outage-center/outage-report-popup/ReconnectPopup';
import { TransitionContext } from '../../providers/TransitionProvider';
import { useOutageLookUp } from './outage-center/hooks/useOutageLookUp';
import useAccountCustomer from '../../hooks/useAccountCustomer';
import DisconnectedReconnectInfoPopup from './outage-center/outage-report-popup/DisconnectedReconnectPopup/DisconnectedReconnectInfoPopup';

interface StyleProps {
  isNativeMobileView: boolean;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    mainArea: {
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      position: 'relative',
    },
    legendButton: {
      position: 'absolute',
      outline: 0,
      background: '#FFFFFF 0% 0% no-repeat padding-box',
      top: '20px',
      right: '10px',
      width: '56px',
      height: '56px',
      padding: 0,
      '&:hover': {
        backgroundColor: colors.white,
      },
      [theme.breakpoints.down('sm')]: {
        top: '80px',
        zIndex: 1,
        width: '40px',
        height: '40px',
      },
    },
    mapLegendIcon: {
      fontSize: '2.5rem',
    },
    AccountCircleIcon: {
      width: '2.2rem',
      height: '1.5rem',
      fontSize: '1.5rem',
    },
    mobAppLegendButton: {
      [theme.breakpoints.down('sm')]: {
        top: '80px',
      },
      backgroundColor: colors.white,
      '&:hover': {
        backgroundColor: colors.white,
      },
    },
    legendButtonIcon: {
      height: 40,
      width: 40,
      padding: 0,
    },
    outageMapHeaderWrapper: {
      width: '34.25vw',
      height: 40,
      backgroundColor: colors.white,
      borderRadius: 5,
      position: 'absolute',
      transform: 'translate(-50% , -50%)',
      top: -34,
      left: '50%',
      zIndex: 2,
      right: '50%',
      [theme.breakpoints.down('sm')]: {
        top: 40,
        width: '95.16vw',
        height: 44,
        boxShadow: `0px 3px 6px ${colors.boxShadowLight}`,
      },
    },
    mobAppOutageMapHeaderWrapper: {
      top: 40,
    },
    reportOutageButton: {
      fontSize: '1rem',
      borderRadius: 5,
      color: colors.white,
      position: 'absolute',
      bottom: '35px',
      left: '50%',
      width: '168px',
      height: '38px',
      transform: 'translate(-50%)',
      background: colors.orange,
      letterSpacing: 0.32,
      boxShadow: `0px 5px 5px ${colors.boxShadowColor}`,
      '&:hover': {
        boxShadow: theme.shadows[5],
        background: colors.orange,
      },
    },
    reportOutageButtonMobileApp: (props: StyleProps) => ({
      position: 'absolute',
      top: `calc(100vh - ${props.isNativeMobileView ? '7.5rem' : '13rem'})`,
      zIndex: 2,
      right: 0,
      padding: 0,
    }),
    reportButton: (props: StyleProps) => ({
      position: 'absolute',
      bottom: 0,
      zIndex: 2,
      width: '100%',
      display: 'flex',
      background: '#fff',
    }),
    outageAndAccountsContainer: {
      position: 'absolute',
      right: '10px',
      top: '160px',
      width: 'auto',
      [theme.breakpoints.down('sm')]: {
        top: '80px',
        left: '10px',
      },
    },
    outageBtn: {
      marginRight: '10px',
    },
    toggleBtn: {
      backgroundColor: colors.white,
      textTransform: 'capitalize',
      fontSize: '0.875rem',
      color: colors.noirBlur,
      borderRadius: '15px',
      minWidth: '110px',
      '&:focus': {
        backgroundColor: colors.white,
      },
      '&:hover': {
        backgroundColor: colors.white,
      },
    },
    detailButton: {
      width: '100%',
      border: '1px solid',
      borderRadius: '0px',
      padding: '5px',
    },
    outageButton: {
      width: '100%',
      border: '1px solid',
      borderRadius: '0px',
      padding: '10px',
    },
    reportOutageButtonMobileAppIcon: {
      '& .a': {
        fill: colors.orange,
        opacity: 1,
      },
      '& .b': {
        fill: colors.white,
        stroke: colors.white,
      },
      '& .d': {
        fontSize: '.625rem',
        fontWeight: 500,
      },
    },
    topStyles: {
      top: -135,
    },
    reportOutageBtnHolder: {
      position: 'relative',
    },
    btnFadeInOut: {
      animation: 'fadeinout 4s infinite',
      '-webkit-animation': 'fadeinout 4s infinite',
      '-moz-animation': 'fadeinout 4s infinite',
      '-o-animation': 'fadeinout 4s infinite',
    },
    '@keyframes fadeinout': {
      from: {
        opacity: 0,
      },
      to: {
        opacity: 1,
      },
    },
  }),
);

export interface Props {
  path: string;
  isNativeMobileAppPage?: boolean;
}
export type OutageDetailInfo = {
  encryptedServicePointList: string[];
  callbackPhone: string;
  encryptedAccountNumber: string;
  encryptedPremiseId: string;
};

export interface NotificationRequestAccountParams {
  accountNumber: string;
  encryptedPersonId: string;
}

type OutageDataResponse = OutageLookupResponse & {
  isLookupSearch?: boolean;
};

export type OutageFindAccountData = {
  isOpen: boolean;
  lookUpType: OutageLookUpType;
  lookUpValue: string;
  isMultiPremise: boolean;
};

export type Coordinates = {
  lat: number;
  lng: number;
};

export const OutageCenterPage: FunctionComponent<Props> = (props: Props) => {
  const { isNativeMobileAppPage } = props;
  const classes = useStyles({
    isNativeMobileView: Boolean(isNativeMobileAppPage),
  });
  const reportOutageBtnRefMobile = useRef<HTMLButtonElement>(null);
  const [reportInfo, setReportInfo] = useState<OutageDetailInfo | null>(null);
  const { isPlannedDownTime } = useContext(featureFlagsContext);
  const { language } = useContext(LanguageContext);
  const { t } = useTranslation();
  const { setState: updateTransition } = useContext(TransitionContext);
  const [
    loggedInUserNotificationRequestAccountParams,
    setLoggedInUserNotificationRequestAccountParams,
  ] = useState<NotificationRequestAccountParams>({
    accountNumber: '',
    encryptedPersonId: '',
  });
  const { content } = useContentMgmt(microcopyGroupIds.OUTAGES_BY_COUNTY);

  const {
    outageData,
    totalCustomersAffected,
    loading,
    accountDetails,
    accountDetailsLoading,
    lastUpdateOutageDataTime,
    refetchAccountDetails,
    setOutageMapNotification,
  } = useOutageEvents(); // use this for to get outage event data
  const { isAuthenticated } = useAuth();
  const {
    getOutageAccountDetails,
    buildLeftPanelDataFromAccountPremise,
    setErrorNotification,
    getRequestSource,
  } = useUtils();
  const { getOutageLookUpQueryResponse } = useOutageLookUp();
  const totalOutageContent: any = content.get('TOTAL_OUTAGES_BY_COUNTY');
  const {
    getOutageEventDetails,
    reportOutageForGuest,
    reportOutageForLoggedInUser,
  } = useReportOutageApi();
  const { fetchContactDetails } = useUpdateInfoApi();
  const { customerContactInfo } = fetchContactDetails();
  const { customer: customerInfoData } = useAccountCustomer();
  const totalOutage =
    totalOutageContent > 0
      ? totalOutageContent
      : outageData?.length
      ? outageData.length
      : 0;
  const totalAccountsInPanel =
    (accountDetails && getOutageAccountDetails(accountDetails)?.length) || 0;

  const totalCustomersImpacted =
    outageData?.length > 0
      ? outageData.reduce(
          (acc, outage) => acc + outage.totalCustomersImpacted,
          0,
        )
      : 0;

  const [outagesAndAccountsPanel, setOutagesAndAccountsPanel] = useState<
    string
  >('');

  const [notificationFormValues, setNotificationFormValues] = useState<Model>({
    receiveEmail: false,
    email: '',
    receiveText: false,
    textNumber: '',
    callbackPhoneNumber: '',
    receiveCallBack: false,
    encryptedEmailContactId: '',
    encryptedTextNumberContactId: '',
  });

  const isMobile = useIsMobile();

  const {
    pgeHeaderHeight,
    globalContainerHeight,
    notificationContainerHeight,
    outageGlobalMessagesContainerHeight,
    notificationVisiblity: isNotificationVisible,
    outageNotificationContainerHeight,
  } = useOutageMapNotificationVisiblity();

  const [mapCenter, setMapCenter] = useState<Coordinates>();

  const primaryAccountWithOutageDetails = accountDetails?.filter(
    account => account && account.isDefault,
  )[0];

  const isOutageReported = Boolean(
    primaryAccountWithOutageDetails &&
      primaryAccountWithOutageDetails?.premiseInfo?.some(premise =>
        premise?.servicePointDetails?.some(servicePoint =>
          Boolean(servicePoint?.outageInfo?.isReported),
        ),
      ),
  );

  const [isOutageResultAltLoc, setIsOutageResultAltLoc] = useState<boolean>(
    isOutageReported,
  );
  const [reportOutageFromIcon, setReportOutageFromIcon] = useState<boolean>(
    false,
  );
  const [isAccRefetch, setIsAccRefetch] = useState<boolean>(false);
  const [outStageState, setOutStageState] = useState<boolean>(false);
  const [isOtherLocation, setOtherLocationType] = useState<boolean>(false);
  const [isAnonymousFlow, setAnonymousFlow] = useState<boolean>(false);
  const [isFAQNoticePopUp, setIsFAQNoticePopUp] = useState<boolean>(false);
  const [isMainPanelEnabled, setIsMainPanelEnabled] = useState<boolean>(true);
  const [isFindAccountData, setIsFindAccountData] = useState<
    OutageFindAccountData
  >({
    isOpen: false,
    lookUpType: OutageLookUpType.Phone,
    lookUpValue: '',
    isMultiPremise: false,
  });
  const onMarkerClick = useCallback((index: number, outage: OutageEvent) => {
    isMobile && setOutStageState(true);
    setIsReportOutagePopup(false);
    void handleOutageEventClick(outage);
  }, []);
  const [isOutageMapLegendPopup, setIsOutageMapLegendPopup] = useState<Boolean>(
    false,
  );

  const [
    isChangeNotificationSettingsPopup,
    setIsChangeNotificationSettingsPopup,
  ] = useState<boolean>(false);

  const [isReportOutagePopup, setIsReportOutagePopup] = useState<boolean>(
    false,
  );
  const [houseNumber, setHouseNumber] = useState<string>('');
  const [isReportOutageConfirm, setIsReportOutageConfirm] = useState<boolean>(
    false,
  );
  const [outageReportedConfirm, setOutageReportedConfirm] = useState(false);

  const [selectOutageType, setSelectOutageType] = useState<boolean>(false);
  const [isDimOrFlickering, setIsDimOrFlickering] = useState<boolean>(false);
  const [outageCheckBreakersPopup, setOutageCheckBreakersPopup] = useState(
    false,
  );
  const [checkBreakerNotWork, setCheckBreakerNotWork] = useState<boolean>(
    false,
  );
  const [showReconnectPopup, setShowReconnectPopup] = useState<boolean>(false);

  const [mapType, setMapType] = useState<string>('roadmap');
  const [showOutageByZipPopup, setShowOutageByZipPopup] = useState<boolean>(
    false,
  );
  const [
    notifFormSuccessfullySubmitted,
    setNotifFormSuccessfullySubmitted,
  ] = useState<boolean>(false);
  const [outageLeftPanelData, setOutageLeftPanelData] = useState<
    OutageResultPanelData
  >({
    outageData: {
      isEligibleToReport: false,
    },
    isReported: false,
    isNotificationSubmit: false,
    isOutageByZipCode: false,
    isOutageByEvent: false,
    isOutageNotFound: false,
    isOutageByLookup: false,
    isOutageByAccount: false,
    shouldRefetchAccount: false,
    selectedCountyName: null,
    selectedZipCode: null,
    selectedZipIndex: null,
    zipCodes: null,
  });
  const [outageDetail, setOutageDetail] = useState<OutageLookupResponse>({
    isAccountExists: true,
    encryptedAccountNumber: '',
    isEligibleToReport: false,
  });
  const [isPremiseSelectionPopup, setIsPremiseSelectionPopup] = useState<
    boolean
  >(false);

  const [selectedEncryptedPremiseId, setSelectedEncryptedPremiseId] = useState<
    string | null | undefined
  >(null);

  const [selectAccount, setSelectAccount] = useState<boolean>(false);
  const [disconnectReconnectPopup, setDisconnectReconnectPopup] = useState<
    boolean
  >(false);

  const [selectedAccount, setSelectedAccount] = useState<AccountDetail | null>(
    null,
  );
  const [selectedPremise, setSelectedPremise] = useState<PremiseInfo | null>(
    null,
  );
  const [isMobileReportOutage, setIsMobileReportOutage] = useState<
    boolean | undefined
  >(false);

  const { wrapWithLoader } = useWrapWithLoader();
  function resetLeftOutageDataPanel() {
    setOutageLeftPanelData({
      outageData: {
        isEligibleToReport: false,
      },
      isReported: false,
      isNotificationSubmit: false,
      isOutageByZipCode: false,
      isOutageByEvent: false,
      isOutageNotFound: false,
      isOutageByLookup: false,
      isOutageByAccount: false,
      shouldRefetchAccount: false,
      selectedCountyName: null,
      selectedZipCode: null,
      selectedZipIndex: null,
      zipCodes: null,
    });
  }

  const getMeterStatusOfDefaultAccount = () => {
    const defaultAccountDetails: AccountDetail = accountDetails?.filter(
      account => account.isDefault,
    )[0] as AccountDetail;
    if (
      accountDetails &&
      defaultAccountDetails?.premiseInfo &&
      defaultAccountDetails?.premiseInfo?.length > 0 &&
      defaultAccountDetails?.premiseInfo[0]?.servicePointDetails &&
      defaultAccountDetails?.premiseInfo[0]?.servicePointDetails.length > 0
    ) {
      return (
        defaultAccountDetails?.premiseInfo[0]?.servicePointDetails[0]?.meter
          ?.status ?? ''
      );
    } else {
      return;
    }
  };
  const updateOutageMapLegendPopup = () => {
    setIsOutageMapLegendPopup(prev => {
      return !prev;
    });

    setOutagesAndAccountsPanel('');
    setIsReportOutagePopup(false);
  };

  const updateChangeNotificationSettingsPopup = () => {
    setIsChangeNotificationSettingsPopup(prev => {
      return !prev;
    });
  };

  const updatePremiseSelectionPopup = () => {
    setIsPremiseSelectionPopup(prev => {
      return !prev;
    });

    if (isMobile) {
      if (isFindAccountData.isMultiPremise) {
        setIsFindAccountData({
          ...isFindAccountData,
          isMultiPremise: false,
          isOpen: true,
        });
        return;
      }
      reportOutageFromIcon && setIsReportOutagePopup(!isReportOutagePopup);
    }
  };

  const faqNoticePopup = () => {
    setIsFAQNoticePopUp(prev => {
      return !prev;
    });
    isMobile && setOutagesAndAccountsPanel('');
  };

  const closeAndShowError = () => {
    setErrorNotification(true);
    typeof window !== 'undefined' && window.scrollTo(0, 0);
  };

  const checkForDues = (accountDetail?: AccountDetail) => {
    if (
      accountDetail &&
      accountDetail?.premiseInfo &&
      accountDetail?.premiseInfo[0] &&
      accountDetail?.premiseInfo[0]?.servicePointDetails &&
      accountDetail?.premiseInfo[0]?.servicePointDetails[0] &&
      accountDetail?.premiseInfo[0]?.servicePointDetails[0]?.meter?.status ===
        'disconnected'
    ) {
      setSelectAccount(false);
      if (accountDetail?.serviceConnectivity?.isEligibleForReconnect) {
        void navigate(ROUTES.RECONNECT_SERVICE);
      } else {
        setDisconnectReconnectPopup(true);
      }
    }
  };

  const handleSelectAccount = (
    event?: any,
    isMobileReportOutageFlag: boolean | undefined = false,
  ) => {
    if (isMobileReportOutageFlag) {
      setIsMobileReportOutage(isMobileReportOutageFlag);
    }
    let meterStatus = null;
    if (accountDetails?.length && accountDetails?.length === 1) {
      meterStatus =
        accountDetails[0]?.premiseInfo &&
        accountDetails[0]?.premiseInfo[0]?.servicePointDetails &&
        accountDetails[0]?.premiseInfo[0]?.servicePointDetails[0] &&
        accountDetails[0]?.premiseInfo[0]?.servicePointDetails[0]?.meter
          ?.status;
    }
    if (
      isAuthenticated &&
      meterStatus === 'disconnected' &&
      accountDetails &&
      accountDetails.length === 1 &&
      !accountDetails[0]?.serviceConnectivity?.isEligibleForReconnect
    ) {
      setDisconnectReconnectPopup(true);
    } else {
      if (
        accountDetails?.length === 1 &&
        accountDetails[0].premiseInfo?.length === 1 &&
        accountDetails[0].premiseInfo[0]
      ) {
        const hasKnownOutage = Boolean(
          accountDetails[0].premiseInfo[0]?.servicePointDetails?.find(point => {
            return point?.outageInfo?.hasKnownOutage;
          }),
        );
        if (hasKnownOutage) {
          setSelectedAccount(null);
          setSelectedPremise(null);
          setOtherLocationType(true);
          setIsReportOutagePopup(true);
        } else {
          setSelectedAccount(accountDetails[0]);
          setSelectedPremise(accountDetails[0].premiseInfo[0]);
          updateReportOutagePopup(
            null,
            isMobileReportOutageFlag,
            accountDetails[0].premiseInfo[0],
            true,
            accountDetails[0],
          );
        }
      } else {
        if (
          accountDetails?.length !== undefined &&
          accountDetails?.length === 1
        ) {
          setSelectedAccount(accountDetails[0]);
          const data: OutageLookupResponse = {
            isAccountExists: true,
            isEligibleToReport: true,
            multiplePremiseList: accountDetails[0]?.premiseInfo?.map(
              singlePremise => ({
                encryptedValue: singlePremise?.encryptedPremiseId || '',
                houseNumber: `${singlePremise?.addressLine1},
            ${singlePremise?.city}, ${singlePremise?.state} ${singlePremise?.postal}`,
                last4CharAddress: '',
                premiseInfo: singlePremise,
              }),
            ),
          };
          const accountIdentifier = `${accountDetails[0]?.description}${
            accountDetails[0]?.isDefault ? ` (${t('PRIMARY')})` : ''
          }`;
          outageLookUpResultSetUp(
            data,
            accountDetails[0]?.accountNumber,
            accountIdentifier,
          );
        }
        if (
          accountDetails?.length !== undefined &&
          accountDetails?.length > 1
        ) {
          setSelectAccount(true);
        }
      }
    }
  };

  const handleSelectAccountAndPremise = (
    accountDetail: AccountDetail,
    premise: PremiseInfo,
    hasKnownOutage: boolean,
    hasMultiplePremises?: boolean,
  ) => {
    setIsPremiseSelectionPopup(false);
    if (
      accountDetail?.premiseInfo &&
      accountDetail?.premiseInfo[0] &&
      accountDetail?.premiseInfo[0]?.servicePointDetails &&
      accountDetail?.premiseInfo[0]?.servicePointDetails[0] &&
      accountDetail?.premiseInfo[0]?.servicePointDetails[0]?.meter?.status ===
        'disconnected'
    ) {
      setSelectAccount(false);
      if (accountDetail?.serviceConnectivity?.isEligibleForReconnect) {
        void navigate(ROUTES.RECONNECT_SERVICE);
      } else {
        setDisconnectReconnectPopup(true);
      }
    } else {
      if (hasMultiplePremises) {
        if (accountDetail) {
          setSelectedAccount(accountDetail);
        }
        const data: OutageLookupResponse = {
          isAccountExists: true,
          isEligibleToReport: true,
          multiplePremiseList: accountDetail?.premiseInfo?.map(
            singlePremise => ({
              encryptedValue: singlePremise?.encryptedPremiseId || '',
              houseNumber: `${singlePremise?.addressLine1},
              ${singlePremise?.city}, ${singlePremise?.state} ${singlePremise?.postal}`,
              last4CharAddress: '',
              premiseInfo: singlePremise,
            }),
          ),
        };
        const accountIdentifier = `${accountDetail?.description}${
          accountDetail?.isDefault ? ` (${t('PRIMARY')})` : ''
        }`;
        outageLookUpResultSetUp(
          data,
          accountDetail?.accountNumber,
          accountIdentifier,
        );
      } else if (premise) {
        if (hasKnownOutage) {
          const accountName = `${accountDetail.accountNumber}${
            accountDetail.isDefault ? ` (${t('PRIMARY')})` : ''
          }`;
          const data = buildLeftPanelDataFromAccountPremise(premise);
          if (data) {
            handleOutageLookUpResult(
              data,
              accountDetail.accountNumber,
              accountName,
            );
            setSelectAccount(false);
          }
        } else {
          setOtherLocationType(false);
          void handleResultPanelClose();
          if (accountDetail) {
            setSelectedAccount(accountDetail);
          }
          setSelectedPremise(premise);
          setSelectAccount(false);
          updateReportOutagePopup(
            null,
            isMobileReportOutage,
            premise,
            false,
            accountDetail,
          );
        }
      } else {
        //TODO: Need to handle the Premise info null case here
      }
    }
  };

  const getMeterStatusOfSelectedPremise = (
    premise: PremiseInfo | undefined,
  ) => {
    if (
      premise?.servicePointDetails &&
      premise?.servicePointDetails?.length > 0
    ) {
      return premise.servicePointDetails[0]?.meter?.status ?? '';
    } else {
      return;
    }
  };

  const updateReportOutagePopup = (
    event?: any,
    isMobileReportOutageFlag: boolean | undefined = false,
    premise?: PremiseInfo,
    isClearLeftPanelData?: boolean,
    accountDetail?: AccountDetail,
  ) => {
    if (isAuthenticated) {
      checkForDues(accountDetail);
      if (
        getMeterStatusOfSelectedPremise(premise) === 'on' ||
        getMeterStatusOfSelectedPremise(premise) === 'powerQuality'
      ) {
        setSelectOutageType(true);
      } else {
        setIsReportOutagePopup(true);
      }
    } else {
      setIsReportOutagePopup(true);
    }
    setOtherLocationType(false);
    setAnonymousFlow(false);
    !isClearLeftPanelData && resetLeftOutageDataPanel();
    setOutagesAndAccountsPanel('');
    setIsOutageMapLegendPopup(false);
    setCheckBreakerNotWork(false);
    setIsFindAccountData({
      ...isFindAccountData,
      isMultiPremise: false,
      isOpen: false,
    });

    setReportOutageFromIcon(
      typeof isMobileReportOutageFlag === 'boolean' && !isMobile
        ? isMobileReportOutageFlag
        : false,
    );
  };
  const updateMapType = (selectedMapType: string) => {
    setMapType(selectedMapType);
  };

  const handleOutageLookUpResult = useCallback(
    (
      outageLookupData: OutageLookupResponse,
      accountNumber: string | undefined = undefined,
      accountIdentifier: string | undefined = undefined,
    ) => {
      if (outageLookupData && outageLookupData.outages) {
        const reportedOutage = outageLookupData.outages.find(
          outage => outage.hasKnownOutage,
        );
        setOutageLeftPanelData({
          ...outageLeftPanelData,
          isOutageByLookup: accountNumber ? false : true,
          isOutageByZipCode: false,
          isOutageByEvent: false,
          isOutageNotFound: false,
          isOutageByAccount: accountNumber ? true : false,
          shouldRefetchAccount: false,
          isNotificationSubmit: false,
          isReported: outageLookupData?.outages[0]?.isReported
            ? outageLookupData?.outages[0]?.isReported
            : false,
          outageData: {
            accountNumber,
            accountIdentifier,
            isEligibleToReport: outageLookupData.isEligibleToReport,
            isAccountExists: outageLookupData.isAccountExists,
            encryptedAccountNumber: outageLookupData.encryptedAccountNumber,
            encryptedPremiseId: outageLookupData.encryptedPremiseId,
            showConnectionInfo:
              outageLookupData.outageStatusWarnings?.showConnectionInfo,
            encryptedServicePointId: reportedOutage
              ? reportedOutage.encryptedServicePointId
              : outageLookupData.outages[0]?.encryptedServicePointId,
            encryptedServicePointIds: outageLookupData?.outages
              ? outageLookupData.outages.reduce((acc: string[], outage) => {
                  if (outage.encryptedServicePointId) {
                    acc.push(outage.encryptedServicePointId);
                  }
                  return acc;
                }, [])
              : undefined,
            hasKnownOutage: Boolean(reportedOutage),
            houseNumber: outageLookupData.houseNumber,
            callbackPhone: reportedOutage
              ? reportedOutage.callbackPhone
              : outageLookupData.outages[0]?.callbackPhone,
            callbackRequested: reportedOutage
              ? reportedOutage.callbackRequested
              : outageLookupData.outages[0]?.callbackRequested,
            estimatedTimeOut: reportedOutage
              ? reportedOutage.estimatedTimeOut
              : outageLookupData.outages[0]?.estimatedTimeOut,
            estimatedTimeOn: reportedOutage
              ? formatEstimatedTimeOn(reportedOutage?.estimatedTimeOn)
              : outageLookupData.outages[0]?.estimatedTimeOn,
            numberOfCustomersAffected: reportedOutage
              ? reportedOutage.customersAffected
              : outageLookupData.outages[0]?.customersAffected,
            numberOfReports: reportedOutage
              ? reportedOutage.totalReports
              : outageLookupData.outages[0]?.totalReports,
            crewDispatchedStatus: reportedOutage
              ? reportedOutage.crewDispatchStatus
              : outageLookupData.outages[0]?.crewDispatchStatus,
            cause: reportedOutage
              ? reportedOutage.cause
              : outageLookupData.outages[0]?.cause,
            eventStatus: reportedOutage
              ? reportedOutage.eventStatus
              : outageLookupData.outages[0]?.eventStatus,
            ...outageLookupData.outageStatusWarnings,
          },
        });
      } else {
        setOutageLeftPanelData({
          ...outageLeftPanelData,
          isOutageByLookup: true,
          isOutageByZipCode: false,
          isOutageByEvent: false,
          isOutageNotFound: false,
          isOutageByAccount: false,
          shouldRefetchAccount: false,
          isNotificationSubmit: false,
          outageData: {
            isEligibleToReport: outageLookupData.isEligibleToReport,
            isAccountExists: outageLookupData.isAccountExists,
          },
        });
      }
      setReportOutageFromIcon(false);
      setIsOutageResultAltLoc(true);
      setIsFindAccountData({
        ...isFindAccountData,
        isMultiPremise: false,
      });
    },
    [],
  );

  useEffect(() => {
    //Zooming into the selected accounts / premise location on the map
    if (
      selectedEncryptedPremiseId &&
      accountDetails &&
      accountDetails.length > 0
    ) {
      let selectedPremiseData: PremiseInfo | null | undefined;
      const accountGroup = accountDetails.find(account => {
        if (account.premiseInfo) {
          const data = account.premiseInfo.find(
            premise =>
              premise?.encryptedPremiseId === selectedEncryptedPremiseId,
          );
          if (data) {
            selectedPremiseData = data;
            return selectedPremiseData;
          }
          return false;
        }
        return false;
      });

      if (selectedPremiseData && selectedPremiseData?.servicePointDetails) {
        const servicePoint = selectedPremiseData?.servicePointDetails[0];
        if (servicePoint?.lat && servicePoint?.long) {
          setMapCenter({
            lat: servicePoint?.lat,
            lng: servicePoint?.long,
          });
        }
      }

      setSelectedEncryptedPremiseId('');
    }
  }, [outageLeftPanelData]);

  const handleOutageEventClick = wrapWithLoader(async (outage: OutageEvent) => {
    try {
      const outageEvtDetsResponse = await getOutageEventDetails({
        outageEventId: outage.eventId,
      });
      const outageEventDetails = outageEvtDetsResponse.getOutageEventDetails;
      if (outageEventDetails) {
        setOutageLeftPanelData({
          ...outageLeftPanelData,
          isOutageByLookup: false,
          isOutageByZipCode: false,
          isOutageByAccount: false,
          shouldRefetchAccount: false,
          isNotificationSubmit: false,
          isOutageByEvent: true,
          isOutageNotFound: false,
          outageData: {
            isEligibleToReport: true,
            hasKnownOutage: true,
            isAccountExists: true,
            estimatedTimeOut: outageEventDetails.estimatedTimeOut,
            estimatedTimeOn: formatEstimatedTimeOn(
              outageEventDetails?.estimatedTimeOn,
            ),
            numberOfCustomersAffected:
              outageEventDetails.totalCustomersImpacted,
            numberOfReports:
              outageEventDetails.noOfReports === 0
                ? 1
                : outageEventDetails.noOfReports,
            crewDispatchedStatus: outageEventDetails.crewDispatchedStatus,
            cause: outageEventDetails.cause,
            eventStatus: outageEventDetails.eventStatus,
          },
        });
        setIsOutageResultAltLoc(true);
      } else if (outageEventDetails === null) {
        setOutageLeftPanelData({
          ...outageLeftPanelData,
          isOutageByLookup: false,
          isOutageByZipCode: false,
          isOutageByAccount: false,
          shouldRefetchAccount: false,
          isNotificationSubmit: false,
          isOutageByEvent: true,
          isOutageNotFound: true,
          outageData: {
            isEligibleToReport: true,
            hasKnownOutage: false,
            isAccountExists: false,
            estimatedTimeOut: null,
            estimatedTimeOn: null,
            numberOfCustomersAffected: null,
            crewDispatchedStatus: null,
            cause: null,
            eventStatus: undefined,
          },
        });
        setIsOutageResultAltLoc(true);
        setOutageMapNotification('warning', 'OUTAGE_NOT_FOUND');
      }
    } catch {
      setErrorNotification(true);
    }
  });

  useEffect(() => {
    if (!isAccRefetch) {
      const accountsDisplay =
        accountDetails && accountDetails.length
          ? getOutageAccountDetails(accountDetails)
          : null;
      if (
        accountsDisplay?.length === 1 &&
        accountsDisplay[0].hasKnownOutage === true
      ) {
        const account = accountsDisplay[0] as any;
        if (account.premiseInfos.length === 1) {
          const premise = account.premiseInfos[0];
          if (premise?.servicePointDetails) {
            const data = buildLeftPanelDataFromAccountPremise(premise);
            const accountName = `${
              account.description ? account.description : account.accountNumber
            }${account.isPrimaryAccount ? ` (${t('PRIMARY')})` : ''}`;
            data &&
              handleOutageLookUpResult(
                data,
                accountsDisplay[0].accountNumber,
                accountName,
              );
            const servicePoint = premise?.servicePointDetails[0];
            if (servicePoint?.lat && servicePoint?.long) {
              setMapCenter({
                lat: servicePoint?.lat,
                lng: servicePoint?.long,
              });
            }
          }
        }
      }
    }
  }, [accountDetails]);

  const handleNotificationSubmit = () => {
    setOutageLeftPanelData({
      ...outageLeftPanelData,
      isNotificationSubmit: true,
    });
  };

  const outageLookUpResultSetUpFromModal = (
    data: OutageLookupResponse,
    isFindAccMultiPremise?: boolean,
  ) => {
    setOutageDetail(data);
    if (isFindAccMultiPremise) {
      outageLookUpResultSetUp(data);
    } else {
      handleOutageLookUpResult(data);
    }
  };
  const outageLookUpResultSetUpFromIcon = (data: OutageLookupResponse) => {
    setReportOutageFromIcon(true);
    outageLookUpResultSetUp(data);
  };
  const outageLookUpResultSetUpFromHeader = (
    data: OutageLookupResponse,
    lookUpType: OutageLookUpType,
    lookUpValue: string,
  ) => {
    setReportOutageFromIcon(false);

    if (!data.isAccountExists && !data.outages) {
      setOtherLocationType(true);
      setIsFindAccountData({
        isOpen: true,
        lookUpType,
        lookUpValue,
        isMultiPremise: false,
      });
    } else {
      outageLookUpResultSetUp({
        ...data,
        isLookupSearch: true,
      });
    }
  };

  const outageLookUpResultSetUp = useCallback(
    (
      data: OutageDataResponse,
      accountNumber: string | undefined = undefined,
      accountIdentifier: string | undefined = undefined,
      houseNumberInfo: string | undefined = undefined,
    ) => {
      setOutageDetail(data);
      if (
        data?.multiplePremiseList !== null &&
        data?.multiplePremiseList !== undefined &&
        data?.multiplePremiseList?.length > 1
      ) {
        setIsPremiseSelectionPopup(true);
        data.isLookupSearch === undefined &&
          isMobile &&
          setReportOutageFromIcon(true);

        if (accountNumber) {
          setOutageLeftPanelData({
            ...outageLeftPanelData,
            isOutageByAccount: true,
            shouldRefetchAccount: false,
            outageData: {
              ...outageLeftPanelData.outageData,
              accountNumber,
              accountIdentifier,
              multiplePremiseList:
                data.multiplePremiseList !== null
                  ? data.multiplePremiseList
                  : outageLeftPanelData.outageData.multiplePremiseList,
              ...data.outageStatusWarnings,
            },
          });
        } else {
          setOutageLeftPanelData({
            ...outageLeftPanelData,
            shouldRefetchAccount: false,
            isOutageByAccount: false,
          });
        }
      } else {
        setIsPremiseSelectionPopup(false);
        const tempRespData: OutageLookupResponse = {
          ...data,
          houseNumber: houseNumberInfo ? houseNumberInfo : data.houseNumber,
        };

        handleOutageLookUpResult(
          tempRespData,
          outageLeftPanelData.isOutageByAccount && !data.isLookupSearch
            ? outageLeftPanelData.outageData.accountNumber
            : undefined || accountNumber,
          outageLeftPanelData.isOutageByAccount && !data.isLookupSearch
            ? outageLeftPanelData.outageData.accountIdentifier
            : undefined || accountIdentifier,
        );

        // if (data?.outages?.find(outage => outage?.hasKnownOutage)) {
        // handleOutageLookUpResult(
        //   tempRespData,
        //   outageLeftPanelData.isOutageByAccount && !data.isLookupSearch
        //     ? outageLeftPanelData.outageData.accountNumber
        //     : undefined,
        //   outageLeftPanelData.isOutageByAccount && !data.isLookupSearch
        //     ? outageLeftPanelData.outageData.accountIdentifier
        //     : undefined,
        // );
        // } else {
        //   if (data.outageStatusWarnings?.showResetInfo) {
        //     const encryptedServicePointIdList = data?.outages?.map(
        //       (service: any) => service?.encryptedServicePointId,
        //     );
        //     setReportInfo({
        //       callbackPhone: '',
        //       encryptedServicePointList: encryptedServicePointIdList || [],
        //       encryptedAccountNumber: data?.encryptedAccountNumber || '',
        //       encryptedPremiseId: '',
        //     });
        //     setSelectOutageType(true);
        //   } else {
        //     if (data?.outages) {
        //       void reportOutageForAnnonymous(
        //         data?.outages?.map(
        //           outage => outage.encryptedServicePointId,
        //         ) as string[],
        //         '',
        //         data?.encryptedAccountNumber || '',
        //         data?.encryptedPremiseId || '',
        //       );
        //     }
        //   }
        // }
      }
    },
    [outageLeftPanelData],
  );
  const isBrowser = !(typeof window === 'undefined');
  const isOutagesMobileApp =
    isBrowser &&
    window.location.pathname.replace(/\/$/, '') === ROUTES.OUTAGES_MOBILE_APP;

  const handleZipcodeClick = (
    countyName: string | null | undefined,
    zipCode?: string | null | undefined,
    zipIndex?: number | null | undefined,
    zipCodes?: Array<ZipCodeInfo> | undefined | null,
  ) => {
    setOutageLeftPanelData({
      ...outageLeftPanelData,
      isOutageByZipCode: true,
      isOutageByLookup: false,
      isOutageByEvent: false,
      isOutageNotFound: false,
      isOutageByAccount: false,
      shouldRefetchAccount: false,
      isNotificationSubmit: false,
      selectedCountyName: countyName,
      selectedZipCode: zipCode ? zipCode : null,
      selectedZipIndex: zipIndex,
      zipCodes: zipCodes,
    });
    setIsOutageResultAltLoc(true);
    setShowOutageByZipPopup(false);
    setOutagesAndAccountsPanel('');
    isMobile && setIsMainPanelEnabled(true);
  };

  const handleResultPanelClose = wrapWithLoader(async () => {
    if (
      isAuthenticated &&
      outageLeftPanelData.isReported &&
      outageLeftPanelData.shouldRefetchAccount &&
      outageLeftPanelData.isOutageByAccount
    ) {
      setIsAccRefetch(true);
      await refetchAccountDetails();
    } else {
      setIsAccRefetch(false);
    }

    setIsOutageResultAltLoc(false);
    setLoggedInUserNotificationRequestAccountParams({
      accountNumber: '',
      encryptedPersonId: '',
    });
    setOutageLeftPanelData({
      outageData: {
        isEligibleToReport: false,
      },
      isReported: false,
      isNotificationSubmit: false,
      isOutageByZipCode: false,
      isOutageByEvent: false,
      isOutageNotFound: false,
      isOutageByLookup: false,
      isOutageByAccount: false,
      shouldRefetchAccount: false,
      selectedCountyName: null,
      selectedZipCode: null,
      selectedZipIndex: null,
      zipCodes: null,
    });
  });

  const handleOutageReported = (isReportSuccess: boolean) => {
    if (!isReportSuccess) {
      setLoggedInUserNotificationRequestAccountParams({
        accountNumber: '',
        encryptedPersonId: '',
      });
      updateReportOutagePopup('', isMobile);
    }

    setOutageLeftPanelData({
      ...outageLeftPanelData,
      isReported: isReportSuccess,
      shouldRefetchAccount: isReportSuccess,
    });
  };

  const updateFindAccountPopup = (
    event?: any,
    isFindAccMultiPremise = false,
  ) => {
    isMobile &&
      isFindAccountData.isOpen &&
      setReportOutageFromIcon(isFindAccMultiPremise);
    setIsFindAccountData({
      ...isFindAccountData,
      isMultiPremise: isFindAccMultiPremise,
      isOpen: !isFindAccountData.isOpen,
    });
  };

  React.useEffect(() => {
    const accountsDisplay =
      accountDetails && accountDetails.length
        ? getOutageAccountDetails(accountDetails)
        : null;

    if (
      accountsDisplay?.length === 1 &&
      accountsDisplay[0].hasKnownOutage === true
    ) {
      const account = accountsDisplay[0] as any;
      if (account.premiseInfos.length === 1) {
        const premise = account.premiseInfos[0];
        if (premise?.servicePointDetails) {
          setLoggedInUserNotificationRequestAccountParams({
            accountNumber: account?.accountNumber,
            encryptedPersonId: account?.encryptedPersonId,
          });
        }
      }
    }
  }, [accountDetails]);

  const globalMessageStyles = `
      #global-notifications-banner, #outage-global-messages-container, #outage-notification-messages-container {
        position: absolute;
        z-index: 1;
        width: 100%;
      }
      #outage-global-messages-container {
        top: ${globalContainerHeight + pgeHeaderHeight}px;
      }
      #global-notifications-banner {
        top: ${globalContainerHeight +
          outageGlobalMessagesContainerHeight +
          pgeHeaderHeight}px;
        margin: ${
          outageGlobalMessagesContainerHeight && isNotificationVisible
            ? '0'
            : '10px 0'
        }
      }
      #outage-notification-messages-container {
        top: ${
          isMobile
            ? 0
            : `${globalContainerHeight +
                pgeHeaderHeight +
                outageGlobalMessagesContainerHeight +
                notificationContainerHeight}px`
        };
        margin: 10px 0;
        z-index: 4;
      }
    `;

  const mobileSearchBarPosition = isMobile
    ? {
        top:
          notificationContainerHeight +
          outageGlobalMessagesContainerHeight +
          35,
      }
    : {};

  const legendIconPosition = {
    top: isMobile
      ? notificationContainerHeight +
        outageGlobalMessagesContainerHeight +
        (!isPlannedDownTime ? 75 : 15)
      : notificationContainerHeight +
        outageGlobalMessagesContainerHeight +
        outageNotificationContainerHeight +
        15,
  };

  const togglerHeight =
    notificationContainerHeight +
    outageGlobalMessagesContainerHeight +
    (!isPlannedDownTime ? 75 : 15);

  const spinnerText = t('LOADING_OUTAGE_INFO_SPINNER');
  const getPersonName = () => {
    const mainName = customerInfoData?.personName || '';
    const name = mainName.split(',');
    if (name.length > 1) {
      const firstName = name[1]?.trim().split(' ');
      return firstName
        ? firstName.length > 1
          ? firstName[1].length > 1
            ? firstName[1]
            : firstName[0]
          : firstName[0]
        : '';
    } else {
      return name[0];
    }
  };
  function getFirstName(): string {
    let selectedAccountNumber = '';
    if (
      outageLeftPanelData &&
      outageLeftPanelData?.outageData &&
      outageLeftPanelData?.outageData?.accountNumber
    ) {
      selectedAccountNumber = outageLeftPanelData?.outageData?.accountNumber;
    } else {
      selectedAccountNumber = getSelectedAccountDetails()?.accountNumber || '';
    }
    const selectedAccountDetails: AccountDetail = accountDetails?.filter(
      account => account?.accountNumber === selectedAccountNumber,
    )[0] as AccountDetail;
    if (selectedAccountDetails?.accountType === 'COM') {
      return selectedAccountDetails?.description || '';
    }
    const mainName = selectedAccountDetails?.mainCustomerName ?? '';
    const name = mainName.split(',');
    if (name.length > 0) {
      const firstName = name[1]?.trim().split(' ');
      return firstName
        ? firstName.length > 1
          ? firstName[1].length > 1
            ? firstName[1]
            : firstName[0]
          : firstName[0]
        : '';
    } else {
      return name[0];
    }
  }
  function getAccountDescription() {
    let selectedAccountNumber = '';
    if (
      outageLeftPanelData &&
      outageLeftPanelData?.outageData &&
      outageLeftPanelData?.outageData?.accountNumber
    ) {
      selectedAccountNumber = outageLeftPanelData?.outageData?.accountNumber;
    } else {
      selectedAccountNumber = getSelectedAccountDetails()?.accountNumber || '';
    }
    const selectedAccountDetails: AccountDetail = accountDetails?.filter(
      account => account?.accountNumber === selectedAccountNumber,
    )[0] as AccountDetail;
    return selectedAccountDetails?.description || '';
  }

  const getAccountType = () => {
    let selectedAccountNumber = '';
    if (
      outageLeftPanelData &&
      outageLeftPanelData?.outageData &&
      outageLeftPanelData?.outageData?.accountNumber
    ) {
      selectedAccountNumber = outageLeftPanelData?.outageData?.accountNumber;
    } else {
      selectedAccountNumber = getSelectedAccountDetails()?.accountNumber || '';
    }
    const selectedAccountDetails: AccountDetail = accountDetails?.filter(
      account => account?.accountNumber === selectedAccountNumber,
    )[0] as AccountDetail;
    return selectedAccountDetails?.accountType;
  };
  const getPrimaryContactNumber = () => {
    let primaryContactNumber = '';
    if (customerContactInfo && customerContactInfo.contactDetails) {
      const contactDetails = customerContactInfo.contactDetails;
      const primaryContactDetails = contactDetails.filter(
        contact => contact?.contactType === 'PNP',
      );
      primaryContactNumber =
        (primaryContactDetails && primaryContactDetails.length > 0
          ? primaryContactDetails[0]?.contactValue
          : '') || '';
    }
    primaryContactNumber = primaryContactNumber
      .replace('(', '')
      .replace(')', '')
      .replace(' ', '')
      .replace('-', '');
    return primaryContactNumber;
  };
  const getPremiseInfoAsString = (premiseInfo: PremiseInfo) => {
    if (outageLeftPanelData?.outageData?.encryptedPremiseId) {
      return outageLeftPanelData?.outageData?.houseNumber;
    }
    const addressline1 = premiseInfo?.addressLine1 || '';
    const addressline2 = premiseInfo?.addressLine2 || '';
    const city = premiseInfo?.city;
    const state = premiseInfo?.state;
    const postal = premiseInfo?.postal;
    const addressLine = `${addressline1}` + (addressline2 && ',');
    return `${addressLine}, \n ${city}, ${state}, ${postal}`;
  };
  const getPremiseInfo = () => {
    if (
      outageLeftPanelData &&
      outageLeftPanelData?.outageData &&
      outageLeftPanelData?.outageData?.accountNumber
    ) {
      const selectedAccountNumber =
        outageLeftPanelData?.outageData?.accountNumber;
      const selectedAccountDetails: AccountDetail = accountDetails?.filter(
        account => account?.accountNumber === selectedAccountNumber,
      )[0] as AccountDetail;

      if (
        selectedAccountDetails &&
        selectedAccountDetails?.premiseInfo &&
        selectedAccountDetails?.premiseInfo?.length > 0
      ) {
        return selectedAccountDetails?.premiseInfo[0] as PremiseInfo;
      } else {
        return;
      }
    } else if (selectedPremise) {
      return selectedPremise;
    } else {
      const defaultAccountDetails: AccountDetail = getSelectedAccountDetails() as AccountDetail;
      return (
        defaultAccountDetails?.premiseInfo &&
        (defaultAccountDetails?.premiseInfo[0] as PremiseInfo)
      );
    }
  };

  const getAcountNumber = () => {
    return (
      getSelectedAccountDetails()?.accountNumber ||
      selectedAccount?.accountNumber ||
      ''
    ); //selectedAccount can also have accountNumber filled in lookup opopup
  };
  const getSelectedAccountDetails = () => {
    const defaultAccountDetails: AccountDetail =
      selectedAccount ||
      (accountDetails?.filter(
        account => account.isDefault,
      )[0] as AccountDetail);
    let selectedAccNumber: string = defaultAccountDetails?.accountNumber;
    if (
      outageLeftPanelData &&
      outageLeftPanelData?.outageData &&
      outageLeftPanelData?.outageData?.accountNumber
    ) {
      selectedAccNumber = outageLeftPanelData?.outageData?.accountNumber;
    }
    return (
      accountDetails &&
      accountDetails.filter(
        account => account.accountNumber === selectedAccNumber,
      )[0]
    );
  };
  const getDisconnectStatusForSelectedAccount = () => {
    const selectedAccountNum = outageLeftPanelData?.outageData?.accountNumber;
    const matchedAccount: AccountDetail = accountDetails?.filter(
      (account: AccountDetail) => account.accountNumber === selectedAccountNum,
    )[0] as AccountDetail;
    return matchedAccount?.serviceConnectivity
      ?.isEligibleForReconnect as boolean;
  };
  const getMeterStatusOfSelectedAccount = () => {
    const selectedAccountDetails: AccountDetail = accountDetails?.filter(
      account =>
        account?.accountNumber ===
        outageLeftPanelData?.outageData?.accountNumber,
    )[0] as AccountDetail;
    const servicePointDetails =
      selectedAccountDetails?.premiseInfo?.find(
        premise => premise?.servicePointDetails,
      )?.servicePointDetails || [];
    if (Boolean(servicePointDetails.length)) {
      return servicePointDetails[0]?.meter?.status ?? '';
    } else if (outageLeftPanelData.outageData.hasOwnProperty('showResetInfo')) {
      return outageLeftPanelData.outageData.showResetInfo
        ? 'on' || 'powerQuality'
        : 'Unknown';
    } else if (
      selectedPremise?.servicePointDetails &&
      selectedPremise?.servicePointDetails[0]?.meter
    ) {
      return (
        selectedPremise?.servicePointDetails &&
        selectedPremise?.servicePointDetails[0]?.meter?.status
      );
    }
    return;
  };

  const getEligibleConnectivityOfSelectedAccount = () => {
    const selectedAccountDetails: AccountDetail = accountDetails?.filter(
      account =>
        account?.accountNumber ===
        outageLeftPanelData?.outageData?.accountNumber,
    )[0] as AccountDetail;
    return selectedAccountDetails?.serviceConnectivity?.isEligibleForReconnect;
  };

  const fetchingLatestPremiseInfo = async (
    encryptedPremiseIdAsParams: string | undefined,
  ) => {
    const params = {
      lookupType: OutageLookUpType.Premise,
      encryptedValue: encryptedPremiseIdAsParams,
    };
    try {
      const result = await getOutageLookUpQueryResponse(params);
      if (
        Boolean(result) &&
        result?.isEligibleToReport !== null &&
        result?.isEligibleToReport !== undefined
      ) {
        if (result && (!isAuthenticated || isOtherLocation)) {
          outageLookUpResultSetUpFromModal(result);
        }
        updateTransition({ open: false });
      } else {
        closeAndShowError();
      }
    } catch {
      closeAndShowError();
    }
  };

  const reportOutageForAnnonymous = async (
    encryptedServicePointList: string[],
    callbackPhone: string,
    encryptedAccountNumber: string,
    encryptedPremiseId: Maybe<string> | undefined,
  ) => {
    updateTransition({ open: true });
    const outageResponse = await reportOutageForGuest({
      encryptedServicePointList: encryptedServicePointList,
      callbackPhone: callbackPhone,

      source: ReportOutageSource.Web,
      encryptedAccountNumber: encryptedAccountNumber,
    });
    if (outageResponse && outageResponse?.reportOutageBySpListGuest?.success) {
      const params = {
        lookupType: OutageLookUpType.Premise,
        encryptedValue: encryptedPremiseId,
      };
      updateTransition({ open: true });
      const result = await getOutageLookUpQueryResponse(params);
      if (
        Boolean(result) &&
        result?.isEligibleToReport !== null &&
        result?.isEligibleToReport !== undefined
      ) {
        outageLookUpResultSetUpFromModal(result, true);
        setOutageReportedConfirm(true);
        updateTransition({ open: false });
      } else {
        closeAndShowError();
      }
    } else {
      closeAndShowError();
    }
  };

  async function reportOutageForLoggedIn() {
    try {
      updateTransition({ open: true });
      const accountNumber =
        loggedInUserNotificationRequestAccountParams.accountNumber ||
        selectedAccount?.accountNumber;

      const encryptedPremiseId =
        outageLeftPanelData.outageData.encryptedPremiseId ||
        selectedPremise?.encryptedPremiseId;

      const encryptedServicePointList = outageLeftPanelData?.outageData
        ?.encryptedServicePointIds
        ? outageLeftPanelData.outageData.encryptedServicePointIds
        : outageLeftPanelData.outageData.encryptedServicePointId
        ? [outageLeftPanelData.outageData.encryptedServicePointId]
        : selectedPremise?.servicePointDetails
        ? selectedPremise.servicePointDetails.map(
            servicePointDetail => servicePointDetail!.encryptedServicePointId,
          )
        : [];

      const encryptedPersonId =
        loggedInUserNotificationRequestAccountParams.encryptedPersonId ||
        selectedAccount?.encryptedPersonId;

      const result = ((await reportOutageForLoggedInUser({
        accountNumber: accountNumber!,
        encryptedPersonId: encryptedPersonId!,
        encryptedPremiseId: encryptedPremiseId!,
        encryptedServicePointList: encryptedServicePointList,
        source: getRequestSource(),
      })) as unknown) as { reportOutageBySpList: ReportOutageResponse };
      if (result?.reportOutageBySpList?.success) {
        setOutageReportedConfirm(true);
        await refetchAccountDetails();

        //Closing outage results panel
        setSelectAccount(false);
        setOtherLocationType(false);
        void handleResultPanelClose();
      } else {
        closeAndShowError();
      }
      updateTransition({ open: false });
    } catch (error) {
      closeAndShowError();
      updateTransition({ open: false });
    }
  }

  return (
    <div className={classes.mainArea}>
      {loading ? (
        <Backdrop forceOpen message={spinnerText} />
      ) : (
        <>
          <style>{globalMessageStyles}</style>
          <OutageCenterMap
            mapData={outageData}
            onMarkerClick={onMarkerClick}
            id="google-outage-map"
            mapType={mapType}
            accountDetails={accountDetails}
            isNativeMobileAppPage={isNativeMobileAppPage}
            outageBannerNotificationHeight={
              notificationContainerHeight + outageGlobalMessagesContainerHeight
            }
            setSinglePremise={handleOutageLookUpResult}
            setMultiPremise={outageLookUpResultSetUp}
            center={mapCenter}
            showSelectedEvent={outageLeftPanelData.isOutageByEvent}
            setLoggedInUserNotificationRequestAccountParams={
              setLoggedInUserNotificationRequestAccountParams
            }
            notifFormSuccessfullySubmitted={notifFormSuccessfullySubmitted}
            setNotifFormSuccessfullySubmitted={
              setNotifFormSuccessfullySubmitted
            }
          />
          {!isPlannedDownTime && (
            <Grid
              className={clsx(
                classes.outageMapHeaderWrapper,
                isOutagesMobileApp ? classes.mobAppOutageMapHeaderWrapper : '',
              )}
              style={mobileSearchBarPosition}
            >
              <OutageMapHeader
                outageLookUpResult={(
                  data: OutageLookupResponse,
                  lookUpType: OutageLookUpType,
                  lookUpValue: string,
                ) => {
                  setOutStageState(false);
                  setAnonymousFlow(true);
                  outageLookUpResultSetUpFromHeader(
                    data,
                    lookUpType,
                    lookUpValue,
                  );
                }}
                setLoggedInUserNotificationRequestAccountParams={
                  setLoggedInUserNotificationRequestAccountParams
                }
              />
            </Grid>
          )}
          <IconButton
            aria-label="legend"
            className={clsx(
              classes.legendButton,
              isOutagesMobileApp ? classes.mobAppLegendButton : '',
            )}
            style={legendIconPosition}
            onClick={updateOutageMapLegendPopup}
          >
            <OutageMapLegendIcon
              viewBox="0 0 40 40"
              className={classes.mapLegendIcon}
            />
          </IconButton>
          {/*{!isPlannedDownTime &&*/}
          {/*  isMobile &&*/}
          {/*  !isReportOutagePopup &&*/}
          {/*  !isPremiseSelectionPopup && (*/}
          {/*    <Button*/}
          {/*      aria-label="report-outage"*/}
          {/*      className={classes.reportOutageButtonMobileApp}*/}
          {/*      onClick={e => updateReportOutagePopup(e, true)}*/}
          {/*      ref={reportOutageBtnRefMobile}*/}
          {/*    >*/}
          {/*      <ReportOutageIcon*/}
          {/*        title={t('REPORT')}*/}
          {/*        xOffset={language === 'en' ? '-14.705' : '-17.705'}*/}
          {/*      />*/}
          {/*    </Button>*/}
          {/*  )}*/}
          {!isPlannedDownTime && isMobile && !isPremiseSelectionPopup && (
            <Card>
              <Grid
                container
                className={classes.outageAndAccountsContainer}
                style={{ top: togglerHeight }}
              >
                <Grid item className={classes.outageBtn}>
                  <Button
                    aria-label="outage"
                    className={classes.toggleBtn}
                    onClick={e => {
                      setIsOutageResultAltLoc(false);
                      setOutStageState(!outStageState);
                      setIsReportOutagePopup(false);
                    }}
                  >
                    <AccountCircleOutlined
                      viewBox="0 0 20 25"
                      className={classes.AccountCircleIcon}
                    />
                    {`${t('OUTAGE')} (${totalOutage})`}
                  </Button>
                </Grid>
                <Grid item>
                  <Button
                    aria-label="report-outage"
                    className={classes.toggleBtn}
                    onClick={
                      isAuthenticated
                        ? (
                            event?: any,
                            isMobileReportOutageFlag:
                              | boolean
                              | undefined = false,
                          ) => {
                            handleSelectAccount(
                              event,
                              isMobileReportOutageFlag,
                            );
                            setIsOutageResultAltLoc(false);
                            setIsReportOutagePopup(!isReportOutagePopup);
                            setOutStageState(true);
                          }
                        : () => {
                            setIsOutageResultAltLoc(false);
                            setIsReportOutagePopup(!isReportOutagePopup);
                            setOutStageState(true);
                          }
                    }
                    ref={reportOutageBtnRefMobile}
                  >
                    <ReportOutageIcon
                      height={'25'}
                      width={'25'}
                      title={t('REPORT')}
                      xOffset={language === 'en' ? '-14.705' : '-17.705'}
                    />
                    {t('REPORT_OUTAGE')}
                  </Button>
                </Grid>
              </Grid>
            </Card>
          )}
          {!isPlannedDownTime && !isMobile && (
            <Button
              aria-label="report-outage"
              className={classes.reportOutageButton}
              onClick={
                isAuthenticated
                  ? handleSelectAccount
                  : () => setIsReportOutagePopup(true)
              }
            >
              {t('REPORT_AN_OUTAGE')}
            </Button>
          )}
          {isOutageMapLegendPopup && (
            <OutageMapLegendPopup
              setMapStyle={updateMapType}
              mapStyle={mapType}
              onClose={updateOutageMapLegendPopup}
            />
          )}
          {isMainPanelEnabled &&
            !reportOutageFromIcon &&
            (!isMobile || !isChangeNotificationSettingsPopup) &&
            (isOutageResultAltLoc ? (
              <OutageResultsPanel
                notificationFormValues={notificationFormValues}
                notifFormSuccessfullySubmitted={notifFormSuccessfullySubmitted}
                setNotificationFormValues={setNotificationFormValues}
                data={outageLeftPanelData}
                isOtherOrAnonymousFlow={
                  isOtherLocation || isAnonymousFlow || !isAuthenticated
                }
                meterStatus={getMeterStatusOfSelectedAccount() || ''}
                isEligibleForReconnect={getDisconnectStatusForSelectedAccount()}
                onClose={() => {
                  setSelectAccount(false);
                  setOtherLocationType(false);
                  void handleResultPanelClose();
                }}
                onReportOutage={handleOutageReported}
                reportOutage={async () => {
                  if (
                    isAuthenticated &&
                    getMeterStatusOfSelectedAccount() === 'disconnected'
                  ) {
                    if (getEligibleConnectivityOfSelectedAccount()) {
                      void navigate(ROUTES.RECONNECT_SERVICE);
                    } else {
                      setDisconnectReconnectPopup(true);
                    }
                  } else {
                    if (
                      (isOtherLocation || isAnonymousFlow) &&
                      outageLeftPanelData?.outageData.showConnectionInfo
                    ) {
                      setDisconnectReconnectPopup(true);
                    } else {
                      if (
                        getMeterStatusOfSelectedAccount() === 'on' ||
                        getMeterStatusOfSelectedAccount() === 'powerQuality'
                      ) {
                        setSelectOutageType(true);
                      } else {
                        if (
                          isAuthenticated &&
                          !isOtherLocation &&
                          !isAnonymousFlow
                        ) {
                          setIsReportOutagePopup(true);
                        } else if (
                          outageLeftPanelData.outageData
                            .encryptedServicePointIds &&
                          outageLeftPanelData.outageData.encryptedAccountNumber
                        ) {
                          void reportOutageForAnnonymous(
                            outageLeftPanelData?.outageData
                              ?.encryptedServicePointIds,
                            '',
                            outageLeftPanelData?.outageData
                              ?.encryptedAccountNumber || '',
                            outageLeftPanelData.outageData.encryptedPremiseId,
                          );
                        }
                      }

                      // if (isAuthenticated && !isOtherLocation && !isAnonymousFlow) {
                      //   if (getMeterStatusOfSelectedAccount() === 'on') {
                      //     setSelectOutageType(true);
                      //   } else {
                      //     setIsReportOutagePopup(true);
                      //   }
                      // } else {
                      //   setHouseNumber(
                      //     outageLeftPanelData?.outageData?.houseNumber || '',
                      //   );
                      //   const params = {
                      //     lookupType: OutageLookUpType.Premise,
                      //     encryptedValue:
                      //       outageLeftPanelData?.outageData?.encryptedPremiseId,
                      //   };
                      //   updateTransition({ open: true });
                      //   const locationResponse = await getOutageLookUpQueryResponse(
                      //     params,
                      //   );
                      //   updateTransition({ open: false });
                      //   const showResetInfo =
                      //     locationResponse?.outageStatusWarnings?.showResetInfo;
                      //   const showConnectionInfo =
                      //     locationResponse?.outageStatusWarnings
                      //       ?.showConnectionInfo;
                      //   if (showResetInfo) {
                      //     if (showConnectionInfo) {
                      //       setShowReconnectPopup && setShowReconnectPopup(true);
                      //     } else {
                      //       setOutageCheckBreakersPopup(true);
                      //       //setSelectOutageType(true);
                      //     }
                      //   } else {
                      //     updateTransition({ open: true });
                      //     void reportOutageForAnnonymous(
                      //       outageLeftPanelData?.outageData
                      //         ?.encryptedServicePointIds as string[],
                      //       '',
                      //       outageLeftPanelData?.outageData
                      //         ?.encryptedAccountNumber || '',
                      //       locationResponse?.encryptedPremiseId,
                      //     );
                      //     updateTransition({ open: false });
                      //   }
                      // }
                    }
                  }
                }}
                handleOutageByZipClick={show => {
                  setShowOutageByZipPopup(show);
                  if (isMobile) {
                    setIsOutageResultAltLoc(false);
                    setIsMainPanelEnabled(false);
                  }
                }}
                handleNotificationPopup={updateChangeNotificationSettingsPopup}
                isChangeNotificationSettingsPopup={
                  isChangeNotificationSettingsPopup
                }
                lastUpdateOutageDataTime={lastUpdateOutageDataTime}
                loggedInUserNotificationRequestAccountParams={
                  loggedInUserNotificationRequestAccountParams
                }
                isNativeMobileAppPage={isNativeMobileAppPage}
              />
            ) : (
              //Left Panel ... default it shows Account Details
              !outStageState && (
                <OutageMapPanel
                  handleOutageByZipClick={show => {
                    setOutagesAndAccountsPanel(t('OUTAGE_HEADER'));
                    setShowOutageByZipPopup(show);
                    isMobile && setIsMainPanelEnabled(false);
                  }}
                  onClose={() => setOutStageState(!outStageState)}
                  setLoggedInUserNotificationRequestAccountParams={
                    setLoggedInUserNotificationRequestAccountParams
                  }
                  setOtherLocationType={() => {
                    resetLeftOutageDataPanel();
                    setSelectedAccount(null);
                    setSelectedPremise(null);
                    setOtherLocationType(true);
                    setAnonymousFlow(false);
                    setIsReportOutagePopup(true);
                  }}
                  isNativeMobileAppPage={isNativeMobileAppPage}
                  accountDetails={accountDetails}
                  accountDetailsLoading={accountDetailsLoading}
                  lastUpdateOutageDataTime={lastUpdateOutageDataTime}
                  totalOutages={outageData ? totalOutage : -1} // total count of ouatges from getAllOutageEvents
                  customersAffected={totalCustomersImpacted} // total count of customers from getAllOutageEvents
                  setSinglePremise={handleOutageLookUpResult}
                  setMultiPremise={outageLookUpResultSetUp}
                  setSelectedEncryptedPremiseId={setSelectedEncryptedPremiseId}
                  faqNoticePopup={faqNoticePopup}
                  updateReportOutagePopup={
                    isAuthenticated
                      ? (
                          event?: any,
                          isMobileReportOutageFlag: boolean | undefined = false,
                        ) => {
                          setOutStageState(true);
                          handleSelectAccount(event, isMobileReportOutageFlag);
                        }
                      : () => {
                          setIsReportOutagePopup(true);
                          setOutStageState(true);
                        }
                  }
                  setNotifFormSuccessfullySubmitted={
                    setNotifFormSuccessfullySubmitted
                  }
                />
              )
            ))}
          {isChangeNotificationSettingsPopup && (
            <ChangeNotificationSettings
              setNotifFormSuccessfullySubmitted={
                setNotifFormSuccessfullySubmitted
              }
              setNotificationFormValues={setNotificationFormValues}
              open={isChangeNotificationSettingsPopup}
              onSubmit={handleNotificationSubmit}
              onClose={updateChangeNotificationSettingsPopup}
              data={outageLeftPanelData}
              loggedInUserNotificationRequestAccountParams={
                loggedInUserNotificationRequestAccountParams
              }
              notificationFormValues={notificationFormValues}
            />
          )}
          {isPremiseSelectionPopup && (
            <MultiplePremise
              open={isPremiseSelectionPopup}
              onClose={updatePremiseSelectionPopup}
              outageDetailData={outageDetail}
              outageLookUpResult={(
                outageLookUpResult: OutageLookupResponse,
                accountNumber?: string,
                accountIdentifier?: string,
                selectedHouseNumber?: string,
                encryptedPremiseId?: string,
              ) => {
                const selectedAccountInfo: AccountDetail = accountDetails?.filter(
                  account =>
                    account.accountNumber ===
                    outageLeftPanelData.outageData.accountNumber,
                )[0] as AccountDetail;
                const selectedPremiseInfo = selectedAccountInfo?.premiseInfo?.filter(
                  premise => premise?.encryptedPremiseId === encryptedPremiseId,
                )[0];
                outageLookUpResultSetUp(
                  outageLookUpResult,
                  accountNumber,
                  accountIdentifier,
                  selectedHouseNumber,
                );

                // if (
                //   (!isAuthenticated || isOtherLocation || isAnonymousFlow) &&
                //   !outageLookUpResult?.outages?.find(
                //     outage => outage.hasKnownOutage,
                //   )
                // ) {
                //   if (outageLookUpResult.outageStatusWarnings?.showResetInfo) {
                //     setSelectOutageType(true);
                //   } else {
                //     if (outageLookUpResult?.outages) {
                //       void reportOutageForAnnonymous(
                //         outageLookUpResult?.outages?.map(
                //           outage => outage.encryptedServicePointId,
                //         ) as string[],
                //         '',
                //         outageLookUpResult?.encryptedAccountNumber || '',
                //         outageLookUpResult?.encryptedPremiseId,
                //       );
                //     }
                //   }
                // }
              }}
              outageFindAccount={isFindAccountData.isMultiPremise}
              reportOutageFromIcon={reportOutageFromIcon}
              setSelectedEncryptedPremiseId={setSelectedEncryptedPremiseId}
              directlySetPremise={Boolean(selectedAccount)} // selectedAccount will have some value if user has come here from bottom report outage button
              selectedAccount={selectedAccount}
              setPremise={handleSelectAccountAndPremise}
            />
          )}
          {isReportOutagePopup &&
            isMobile &&
            (getMeterStatusOfDefaultAccount() !== 'on' ||
            getMeterStatusOfDefaultAccount() !== 'powerQuality' ||
            isOtherLocation ||
            checkBreakerNotWork ? (
              <OutageReportPopupMobApp
                open={isReportOutagePopup}
                primaryContactNumber={
                  isAuthenticated && !isOtherLocation
                    ? getPrimaryContactNumber()
                    : ''
                }
                reportInfo={(param: OutageDetailInfo) => {
                  setReportInfo(param);
                }}
                premiseInfo={
                  getPremiseInfoAsString(getPremiseInfo() as PremiseInfo) || ''
                }
                name={getAccountDescription()}
                isOtherLocation={isOtherLocation}
                setShowReconnectPopup={() => {
                  setShowReconnectPopup(true);
                  setIsReportOutagePopup(false);
                }}
                accontNumber={getAcountNumber()}
                reportOutageForLoggedIn={reportOutageForLoggedIn}
                onPrevious={(
                  addressNumber?: string,
                  showResetInfo?: boolean,
                ) => {
                  if (addressNumber) {
                    setHouseNumber(addressNumber);
                  } else {
                    setHouseNumber('');
                  }
                  if (
                    !isAuthenticated ||
                    isOtherLocation ||
                    (isAuthenticated &&
                      getMeterStatusOfSelectedAccount() === 'on') ||
                    getMeterStatusOfSelectedAccount() === 'powerQuality'
                  ) {
                    if (showResetInfo) {
                      setSelectOutageType(true);
                    }
                    setIsReportOutagePopup(false);
                  } else {
                    setCheckBreakerNotWork(false);
                    setIsReportOutagePopup(false);
                    setOutageCheckBreakersPopup(false);
                  }
                }}
                setHouseNumber={(addressNumber: string) => {
                  if (addressNumber) {
                    setHouseNumber(addressNumber);
                  } else {
                    setHouseNumber('');
                  }
                  setIsReportOutagePopup(false);
                  setOutageReportedConfirm(true);
                }}
                onClose={() => {
                  setIsReportOutagePopup(false);
                  setSelectOutageType(false);
                  setIsReportOutageConfirm(false);
                  setOutageCheckBreakersPopup(false);
                }}
                outageLookUpResult={(
                  data?: OutageLookupResponse,
                  isMultiPremise?: boolean,
                  showSuccess: boolean = true,
                ) => {
                  if (
                    data &&
                    (!isAuthenticated || isOtherLocation || isAnonymousFlow)
                  ) {
                    outageLookUpResultSetUpFromModal(data, isMultiPremise);
                  }
                  setIsReportOutagePopup(false);
                  if (showSuccess) {
                    setOutageReportedConfirm(true);
                  }
                }}
                setLoggedInUserNotificationRequestAccountParams={
                  setLoggedInUserNotificationRequestAccountParams
                }
              />
            ) : (
              <OutageCheckBreakersReportPopupMob
                open={true}
                onClose={() => {
                  setIsReportOutagePopup(false);
                  setSelectOutageType(true);
                  setIsReportOutageConfirm(false);
                }}
                onPrevious={(toPopup?: string) => {
                  setIsReportOutagePopup(false);
                  setIsReportOutageConfirm(false);
                  setOutageCheckBreakersPopup(false);
                  if (toPopup === 'OutageType') {
                    setSelectOutageType(true);
                  }
                  if (toPopup === 'ReportOutage') {
                    setIsReportOutagePopup(true);
                  }
                }}
                onReset={() => {
                  setIsReportOutagePopup(false);
                  setSelectOutageType(false);
                  setIsReportOutageConfirm(true);
                }}
                onProceed={() => {
                  setCheckBreakerNotWork(true);
                }}
              />
            ))}
          {!isMobile &&
            (isReportOutagePopup ||
              checkBreakerNotWork ||
              getMeterStatusOfDefaultAccount() !== 'on' ||
              getMeterStatusOfDefaultAccount() !== 'powerQuality') && (
              <OutageReportPopup
                open={isReportOutagePopup}
                primaryContactNumber={
                  isAuthenticated && !isOtherLocation
                    ? getPrimaryContactNumber()
                    : ''
                }
                reportInfo={(param: OutageDetailInfo) => {
                  setReportInfo(param);
                }}
                premiseInfo={
                  getPremiseInfoAsString(getPremiseInfo() as PremiseInfo) || ''
                }
                name={getAccountDescription()}
                isOtherLocation={isOtherLocation}
                setShowReconnectPopup={() => {
                  setShowReconnectPopup(true);
                  setIsReportOutagePopup(false);
                }}
                accontNumber={getAcountNumber()}
                reportOutageForLoggedIn={reportOutageForLoggedIn}
                onPrevious={(
                  addressNumber?: string,
                  showResetInfo?: boolean,
                ) => {
                  if (addressNumber) {
                    setHouseNumber(addressNumber);
                  } else {
                    setHouseNumber('');
                  }
                  if (
                    !isAuthenticated ||
                    isOtherLocation ||
                    (isAuthenticated &&
                      getMeterStatusOfSelectedAccount() === 'on') ||
                    getMeterStatusOfSelectedAccount() === 'powerQuality'
                  ) {
                    if (showResetInfo) {
                      setSelectOutageType(true);
                    }
                    setIsReportOutagePopup(false);
                  } else {
                    setCheckBreakerNotWork(false);
                    setIsReportOutagePopup(false);
                    setOutageCheckBreakersPopup(false);
                  }
                }}
                setHouseNumber={(addressNumber: string) => {
                  if (addressNumber) {
                    setHouseNumber(addressNumber);
                  } else {
                    setHouseNumber('');
                  }
                  setIsReportOutagePopup(false);
                  setOutageReportedConfirm(true);
                }}
                onClose={() => {
                  setIsReportOutagePopup(false);
                  setSelectOutageType(false);
                  setIsReportOutageConfirm(false);
                  setOutageCheckBreakersPopup(false);
                }}
                outageLookUpResult={(
                  data?: OutageLookupResponse,
                  isMultiPremise?: boolean,
                  showSuccess: boolean = true,
                ) => {
                  if (
                    data &&
                    (!isAuthenticated || isOtherLocation || isAnonymousFlow)
                  ) {
                    outageLookUpResultSetUpFromModal(data, isMultiPremise);
                  }
                  setIsReportOutagePopup(false);
                  if (showSuccess) {
                    setOutageReportedConfirm(true);
                  }
                }}
                setLoggedInUserNotificationRequestAccountParams={
                  setLoggedInUserNotificationRequestAccountParams
                }
              />
            )}
          {disconnectReconnectPopup && (
            <DisconnectedReconnectInfoPopup
              open={true}
              onClose={() => {
                setDisconnectReconnectPopup(false);
              }}
            />
          )}
          {showReconnectPopup && (
            <ReconnectPopup
              onClose={() => {
                setShowReconnectPopup(false);
              }}
            />
          )}
          {isMobile && !isReportOutagePopup && outageCheckBreakersPopup && (
            <OutageCheckBreakersReportPopupMob
              open={outageCheckBreakersPopup}
              onClose={() => {
                setIsReportOutagePopup(false);
                setSelectOutageType(false);
                setIsReportOutageConfirm(false);
                setOutageCheckBreakersPopup(false);
              }}
              onPrevious={() => {
                setIsReportOutagePopup(false);
                setIsReportOutageConfirm(false);
                setOutageCheckBreakersPopup(false);
                if (isOtherLocation || !isAuthenticated) {
                  setIsReportOutagePopup(true);
                } else {
                  setSelectOutageType(true);
                }
              }}
              onProceed={async () => {
                setSelectOutageType(false);
                setOutageCheckBreakersPopup(false);
                if (isAuthenticated && !isOtherLocation && !isAnonymousFlow) {
                  // setIsReportOutagePopup(true);
                  void reportOutageForLoggedIn();
                } else {
                  if (reportInfo) {
                    void reportOutageForAnnonymous(
                      reportInfo.encryptedServicePointList,
                      reportInfo.callbackPhone,
                      reportInfo.encryptedAccountNumber,
                      reportInfo.encryptedPremiseId,
                    );
                  } else {
                    setHouseNumber(
                      outageLeftPanelData?.outageData?.houseNumber || '',
                    );
                    void reportOutageForAnnonymous(
                      outageLeftPanelData?.outageData
                        ?.encryptedServicePointIds as string[],
                      '',
                      outageLeftPanelData?.outageData?.encryptedAccountNumber ||
                        '',
                      outageLeftPanelData?.outageData?.encryptedPremiseId,
                    );
                  }
                }
              }}
              onReset={() => {
                setIsReportOutagePopup(false);
                setSelectOutageType(false);
                setOutageCheckBreakersPopup(false);
                setIsReportOutageConfirm(true);
              }}
            />
          )}
          {!isMobile && outageCheckBreakersPopup && (
            <OutageCheckBreakersPopup
              open={outageCheckBreakersPopup}
              onClose={() => {
                setIsReportOutagePopup(false);
                setSelectOutageType(false);
                setIsReportOutageConfirm(false);
                setOutageCheckBreakersPopup(false);
              }}
              onPrevious={() => {
                setIsReportOutagePopup(false);
                setIsReportOutageConfirm(false);
                setOutageCheckBreakersPopup(false);
                setSelectOutageType(true);
                // if (isOtherLocation || !isAuthenticated) {
                //   setIsReportOutagePopup(true);
                // } else {
                //   setSelectOutageType(true);
                // }
              }}
              onProceed={async () => {
                setSelectOutageType(false);
                setOutageCheckBreakersPopup(false);
                // if (isAuthenticated && !isOtherLocation && !isAnonymousFlow) {
                //   setIsReportOutagePopup(true);
                // } else {
                if (isAuthenticated && !isOtherLocation && !isAnonymousFlow) {
                  void reportOutageForLoggedIn();
                } else if (reportInfo) {
                  void reportOutageForAnnonymous(
                    reportInfo.encryptedServicePointList,
                    reportInfo.callbackPhone,
                    reportInfo.encryptedAccountNumber,
                    reportInfo.encryptedPremiseId,
                  );
                } else {
                  setHouseNumber(
                    outageLeftPanelData?.outageData?.houseNumber || '',
                  );
                  void reportOutageForAnnonymous(
                    outageLeftPanelData?.outageData
                      ?.encryptedServicePointIds as string[],
                    '',
                    outageLeftPanelData?.outageData?.encryptedAccountNumber ||
                      '',
                    outageLeftPanelData?.outageData?.encryptedPremiseId,
                  );
                }
                // }
              }}
              onReset={() => {
                setIsReportOutagePopup(false);
                setSelectOutageType(false);
                setOutageCheckBreakersPopup(false);
                setIsReportOutageConfirm(true);
              }}
            />
          )}
          {outageReportedConfirm && (
            <OutageReportedConfirmPopup
              name={getAccountDescription()}
              accontNumber={getAcountNumber()}
              // houseNumber={houseNumber}
              houseNumber={
                houseNumber ||
                outageLeftPanelData?.outageData?.houseNumber ||
                ''
              }
              isAnonymousOrOtherLocation={
                isOtherLocation || isAnonymousFlow || !isAuthenticated
              }
              onClose={async () => {
                setOutageReportedConfirm(false);
                setAnonymousFlow(false);
                setOutageMapNotification('success', 'REPORT_SUCCESSFUL');
                setOutageReportedStatusForMedallia(1);
                isAuthenticated && (await refetchAccountDetails());
              }}
              premiseInfo={
                getPremiseInfoAsString(getPremiseInfo() as PremiseInfo) || ''
              }
            />
          )}
          {!isMobile && isReportOutageConfirm ? (
            <ResetBreakerConfirm
              open={isReportOutageConfirm}
              onClose={() => {
                setIsReportOutagePopup(false);
                setIsReportOutageConfirm(false);
              }}
            />
          ) : null}
          {isMobile && isReportOutageConfirm ? (
            <ResetBreakerConfirmMob
              onClose={() => {
                setIsReportOutagePopup(false);
                setIsReportOutageConfirm(false);
              }}
              open={isReportOutageConfirm}
            />
          ) : null}

          {selectAccount &&
            (isMobile ? (
              <SelectAccountMob
                onClose={() => {
                  setSelectAccount(false);
                }}
                accounts={accountDetails || []}
                handleSelectAccountAndPremise={handleSelectAccountAndPremise}
              />
            ) : (
              <SelectAccount
                onClose={() => {
                  setSelectAccount(false);
                }}
                accounts={accountDetails || []}
                handleSelectAccountAndPremise={handleSelectAccountAndPremise}
              />
            ))}

          {isMobile && selectOutageType ? (
            <OutageTypeMob
              open={selectOutageType}
              name={getPersonName()}
              onClose={() => {
                setIsReportOutagePopup(false);
                setSelectOutageType(false);
                setIsReportOutageConfirm(false);
              }}
              onClick={type => {
                if (type === 'fullOrPartialOutageMob') {
                  setOutageCheckBreakersPopup(true);
                  setSelectOutageType(false);
                } else {
                  setIsDimOrFlickering(true);
                  setSelectOutageType(false);
                }
              }}
            />
          ) : null}

          {!isMobile && selectOutageType ? (
            <OutageType
              open={selectOutageType}
              name={getPersonName()}
              onClose={() => {
                setIsReportOutagePopup(false);
                setSelectOutageType(false);
                setIsReportOutageConfirm(false);
              }}
              onClick={type => {
                if (type === 'fullOrPartialOutage') {
                  setOutageCheckBreakersPopup(true);
                  setSelectOutageType(false);
                } else {
                  setIsDimOrFlickering(true);
                  setSelectOutageType(false);
                }
              }}
            />
          ) : null}
          {isMobile && isDimOrFlickering ? (
            <DimOrFlickeringMob
              open={isDimOrFlickering}
              onClose={() => {
                setIsDimOrFlickering(flag => !flag);
              }}
            />
          ) : null}
          {!isMobile && isDimOrFlickering ? (
            <DimOrFlickering
              open={isDimOrFlickering}
              onClose={() => {
                setIsDimOrFlickering(flag => !flag);
              }}
            />
          ) : null}
          {showOutageByZipPopup && (
            <OutageListAndZipPanel
              showOutageByZipPopup={showOutageByZipPopup}
              handleOutageByZipClick={show => {
                setShowOutageByZipPopup(show);
                isMobile && setIsMainPanelEnabled(!show);
              }}
              handleZipcodeClick={handleZipcodeClick}
              totalOutages={totalOutage}
              handleMobileOutageListPanel={() => {
                setOutagesAndAccountsPanel('');
                setShowOutageByZipPopup(false);
                setIsOutageMapLegendPopup(false);
                isMobile && setIsMainPanelEnabled(true);
              }}
            />
          )}
          {/* {isMobile && (
            <OutagesAndAccountsToggler
              handleOutageAccountToggle={showPanel => {
                if (showPanel === t('ACCOUNTS')) {
                  setIsMainPanelEnabled(true);
                  setIsOutageResultAltLoc(false);
                  setShowOutageByZipPopup(false);
                  setIsChangeNotificationSettingsPopup(false);
                }
                if (showPanel === outagesAndAccountsPanel) {
                  setOutagesAndAccountsPanel('');
                  setShowOutageByZipPopup(false);
                  setIsOutageResultAltLoc(false);
                  setIsMainPanelEnabled(true);
                  return;
                }
                if (showPanel === t('OUTAGE_HEADER')) {
                  setShowOutageByZipPopup(true);
                  setIsMainPanelEnabled(false);
                }
                setOutagesAndAccountsPanel(showPanel);
                setIsReportOutagePopup(false);
                setIsOutageMapLegendPopup(false);
                setIsFAQNoticePopUp(false);
              }}
              totalOutages={totalOutage}
              totalAccounts={totalAccountsInPanel}
              topHeight={togglerHeight}
            />
          )} */}
          {isFAQNoticePopUp && (
            <OutageMapFaqModal
              open={isFAQNoticePopUp}
              onClose={faqNoticePopup}
            />
          )}
          {isFindAccountData.isOpen && (
            <OutageFindAccountPopup
              data={isFindAccountData}
              onClose={updateFindAccountPopup}
              outageLookUpResult={outageLookUpResultSetUpFromModal}
              setLoggedInUserNotificationRequestAccountParams={
                setLoggedInUserNotificationRequestAccountParams
              }
              isOtherLocation={isOtherLocation}
              reportOutageForLoggedIn={reportOutageForLoggedIn}
            />
          )}
        </>
      )}
    </div>
  );
};

export default OutageCenterPage;
